/**
 * EntityPicker - Wiederverwendbare Autocomplete-Komponente für Entities
 * Sucht in juristische_personen (Institutionen) und natuerliche_personen (Personen)
 * Props:
 *   entityType: "institution" | "person" | "ansprechpartner"
 *   value: { id, name } oder null
 *   onChange: (entity) => void  — entity = { id, name, ... } oder null
 *   institutionId: string (optional, für Ansprechpartner-Filter)
 *   label: string
 *   placeholder: string
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Building2, UserCircle, Search, X, Users, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function EntityPicker({
  entityType = 'institution',
  value = null,
  onChange,
  institutionId,
  label,
  placeholder,
  className = '',
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const typeConfig = {
    institution: { icon: Building2, color: 'text-teal-600 bg-teal-50', placeholder: 'Institution suchen...' },
    person: { icon: UserCircle, color: 'text-purple-600 bg-purple-50', placeholder: 'Person suchen...' },
    ansprechpartner: { icon: Users, color: 'text-sky-600 bg-sky-50', placeholder: 'Ansprechpartner suchen...' },
  };
  const config = typeConfig[entityType] || typeConfig.person;
  const Icon = config.icon;

  const doSearch = useCallback(async (searchQuery) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ typ: entityType, q: searchQuery });
      if (institutionId && entityType === 'ansprechpartner') {
        params.set('institution_id', institutionId);
      }
      const token = localStorage.getItem('olympus_token');
      const res = await fetch(`${API}/api/netzwerk/entity-picker?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setResults(data.results || []);
      }
    } catch (e) {
      console.error('EntityPicker search error:', e);
    } finally {
      setLoading(false);
    }
  }, [entityType, institutionId]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(query), 200);
    return () => clearTimeout(debounceRef.current);
  }, [query, open, doSearch]);

  // Click outside close
  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (entity) => {
    onChange?.(entity);
    setQuery('');
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.(null);
    setQuery('');
  };

  const handleInputFocus = () => {
    setOpen(true);
    if (results.length === 0) doSearch('');
  };

  // Wenn ein Wert ausgewählt ist, zeige die Auswahl
  if (value?.id) {
    return (
      <div className={`relative ${className}`}>
        {label && <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>}
        <div
          className="flex items-center gap-2 border rounded-md px-3 py-2 bg-white cursor-pointer hover:border-slate-400 transition-colors"
          onClick={() => { onChange?.(null); setTimeout(() => inputRef.current?.focus(), 50); }}
          data-testid={`entity-picker-selected-${entityType}`}
        >
          <div className={`w-7 h-7 rounded flex items-center justify-center flex-shrink-0 ${config.color}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{value.name}</p>
            {value.subtext && <p className="text-xs text-slate-500 truncate">{value.subtext}</p>}
          </div>
          <button
            onClick={handleClear}
            className="p-1 text-slate-400 hover:text-red-500 rounded transition-colors"
            data-testid={`entity-picker-clear-${entityType}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      {label && <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={handleInputFocus}
          placeholder={placeholder || config.placeholder}
          className="pl-9 pr-8"
          data-testid={`entity-picker-input-${entityType}`}
        />
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      </div>

      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid={`entity-picker-dropdown-${entityType}`}>
          {loading ? (
            <div className="py-4 text-center text-sm text-slate-400">Suche...</div>
          ) : results.length > 0 ? (
            results.map((r) => (
              <button
                key={r.id}
                onClick={() => handleSelect(r)}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 text-left transition-colors border-b border-slate-50 last:border-0"
                data-testid={`entity-picker-option-${r.id}`}
              >
                <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${
                  r.typ === 'juristische_person' ? 'bg-teal-50 text-teal-600' : 'bg-purple-50 text-purple-600'
                }`}>
                  {r.typ === 'juristische_person' ? <Building2 className="w-4 h-4" /> : <UserCircle className="w-4 h-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{r.name}</p>
                  <p className="text-xs text-slate-500 truncate">{r.subtext}</p>
                </div>
                {r.ansprechpartner_count > 0 && (
                  <span className="text-xs text-slate-400 flex-shrink-0">{r.ansprechpartner_count} AP</span>
                )}
              </button>
            ))
          ) : (
            <div className="py-4 text-center text-sm text-slate-400">
              {query ? 'Keine Ergebnisse' : 'Tippen zum Suchen'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
